/* Tu Guante Ideal — motor de recomendación.
 *
 * Determinista a propósito: no hay LLM en esta página. El bot de WhatsApp ya inventó
 * producto, precio y links 404 una vez (LINK GUARD, 2026-07-16); una página cuyo único
 * trabajo es recomendar producto no puede permitirse ese modo de falla. Todo lo que se
 * muestra sale de window.UK_GLOVES, que se genera del catálogo real.
 *
 * La doctrina (qué recomendar y por qué) sale del KB del bot:
 *   ~/UnoKeeperWhatsApp/kb/como-elegir-guantes.md  — las cinco variables, la regla de los dos pares
 *   ~/UnoKeeperWhatsApp/kb/cortes-de-guante.md     — ningún corte ataja mejor; negativo pide media talla más
 */

const G = (window.UK_GLOVES || []).filter((g) => g.stock > 0);

/* ── Bandas de precio: derivadas del catálogo real, no inventadas ───────────── */
const BANDS = {
  entrada: [0, 900],
  media: [900, 1500],
  alta: [1500, 2100],
  tope: [2100, Infinity],
};
const BAND_ORDER = ['entrada', 'media', 'alta', 'tope'];

/* Nivel del portero → nivel de producto que le corresponde, en orden de preferencia. */
const NIVEL_FIT = {
  empezando: ['semi', 'entrada', 'pro'],
  regular: ['semi', 'pro', 'entrada'],
  competitivo: ['pro', 'semi', 'entrada'],
};

/* Tabla de tallas — misma que snippets/size-finder.liquid. Si una cambia, cambia la otra. */
const SIZES = [
  { size: 3, min: 12.8, max: 13.8 }, { size: 4, min: 13.9, max: 15.0 },
  { size: 5, min: 15.1, max: 15.8 }, { size: 6, min: 15.9, max: 16.7 },
  { size: 7, min: 16.8, max: 17.5 }, { size: 8, min: 17.6, max: 18.6 },
  { size: 9, min: 18.7, max: 19.7 }, { size: 10, min: 19.8, max: 20.8 },
  { size: 11, min: 20.9, max: 22.0 },
];

const CUT_LABEL = {
  negativo: 'corte negativo',
  hibrido: 'corte híbrido',
  roll: 'corte roll finger',
  flat: 'corte plano',
};

/* El roll puro escasea en catálogo (10 piezas). El híbrido es lo más cercano: dedos
 * envueltos en látex, pero con costura interna. Degradar a híbrido es honesto. */
const CUT_NEAR = { roll: ['hibrido'], negativo: ['hibrido'], hibrido: ['negativo', 'roll'], flat: ['roll'] };

const money = (n) => '$' + n.toLocaleString('es-MX');

/* ── Motor ──────────────────────────────────────────────────────────────────── */

function scoreGlove(g, a) {
  let s = 0;

  // Presupuesto: el eje más fuerte. En banda = 100; una banda de distancia aún compite.
  const bi = BAND_ORDER.indexOf(a.presupuesto);
  const gi = BAND_ORDER.findIndex((b) => g.p >= BANDS[b][0] && g.p < BANDS[b][1]);
  const bandDist = Math.abs(bi - gi);
  if (bandDist === 0) s += 100;
  else if (bandDist === 1) s += 45;
  else return -1; // dos bandas fuera: no es lo que pidió

  // Nivel: qué tan bien le queda al portero que dijo ser.
  const fit = NIVEL_FIT[a.nivel] || [];
  const ni = fit.indexOf(g.nivel);
  if (ni === 0) s += 60;
  else if (ni === 1) s += 30;
  else if (g.nivel === null) s += 12; // sin dato: no lo premiamos ni lo castigamos de más

  // Corte, solo si lo pidió. Exacto pesa; el cercano pesa la mitad.
  if (a.corte) {
    if (g.corte === a.corte) s += 40;
    else if ((CUT_NEAR[a.corte] || []).includes(g.corte)) s += 20;
    else if (g.corte === null) s += 5;
  }

  // Superficie y clima: cobertura baja (38%/34%), así que solo suman, nunca filtran.
  if (a.superficie === 'sintetico' || a.superficie === 'dura') {
    if ((g.sup || []).includes('sintetico') || (g.sup || []).includes('dura')) s += 18;
  }
  if (a.superficie === 'natural' && (g.sup || []).includes('natural')) s += 12;
  if (g.wet && (a.superficie === 'natural' || a.superficie === 'todas')) s += 6;

  // Desempate: stock sano primero — recomendar lo que sí podemos surtir en tallas.
  s += Math.min(g.stock, 40) / 10;

  return s;
}

function pool(a) {
  const junior = a.para === 'nino';
  return G.filter((g) => (junior ? g.nivel === 'junior' : g.nivel !== 'junior'));
}

function recommend(a) {
  const cands = pool(a)
    .map((g) => ({ g, s: scoreGlove(g, a) }))
    .filter((x) => x.s > 0)
    .sort((x, y) => y.s - x.s);

  if (!cands.length) return null;

  const primary = cands[0].g;
  // Alternativas: distinto modelo, y variamos marca cuando se pueda — somos multimarca,
  // no tiene sentido enseñar tres colores del mismo guante.
  const alts = [];
  const seenVendor = new Set([primary.v]);
  for (const { g } of cands.slice(1)) {
    if (alts.length >= 3) break;
    if (g.h === primary.h) continue;
    if (seenVendor.has(g.v) && alts.length < 2 && cands.length > 6) continue;
    seenVendor.add(g.v);
    alts.push(g);
  }
  while (alts.length < 3) {
    const extra = cands.slice(1).map((x) => x.g).find((g) => g.h !== primary.h && !alts.includes(g));
    if (!extra) break;
    alts.push(extra);
  }

  return { primary, alts, second: secondPair(a, primary) };
}

/* Regla de los dos pares. Del KB: "una sesión de entrenamiento desgasta el guante lo
 * equivalente a 3 a 5 partidos… el estándar entre porteros es tener DOS pares".
 * Solo aplica si de verdad juega lo suficiente — a quien juega los domingos no le
 * vendemos un segundo par que no necesita. */
function secondPair(a, primary) {
  const juegaMucho = a.frecuencia === 'media' || a.frecuencia === 'alta';
  const abrasivo = a.superficie === 'sintetico' || a.superficie === 'dura';
  const caro = primary.p >= 1500;

  if (!juegaMucho) return null;
  if (!caro && !abrasivo) return null;

  // El par de entrenar: más barato que el titular, mismo perfil de usuario, durable.
  const target = primary.p >= 2100 ? 'media' : 'entrada';
  const cands = pool(a)
    .filter((g) => g.h !== primary.h)
    .filter((g) => g.p >= BANDS[target][0] && g.p < BANDS[target][1])
    .filter((g) => g.p < primary.p)
    .map((g) => {
      let s = 0;
      if ((g.sup || []).includes('sintetico')) s += 20;
      if (a.corte && g.corte === a.corte) s += 15; // que se sienta parecido al bueno
      if (g.nivel === 'semi' || g.nivel === 'entrada' || g.nivel === 'junior') s += 10;
      s += Math.min(g.stock, 40) / 10;
      return { g, s };
    })
    .sort((x, y) => y.s - x.s);

  if (!cands.length) return null;

  const razon = abrasivo
    ? 'Juegas en una superficie que se come las palmas y entrenas seguido. Si quemas el bueno en los entrenamientos, te va a durar semanas.'
    : 'Con lo que entrenas, un solo par de gama alta se te va rápido. Un entrenamiento desgasta como 3 a 5 partidos.';

  return {
    g: cands[0].g,
    titulo: 'Te va a salir más barato con dos pares',
    body: razon + ' Entrena con este y guarda el bueno para el partido — repartes el desgaste y los dos te duran más.',
  };
}

/* El "por qué": honesto y atado a lo que el portero contestó. Nunca genérico. */
function buildWhy(a, g) {
  const r = [];

  if (a.para === 'nino') {
    r.push('Es un guante <strong>junior</strong>, con la construcción pensada para manos que todavía están creciendo.');
  }

  const nivelTxt = {
    empezando: 'Vas empezando, así que priorizamos que <strong>aguante los entrenamientos y ajuste bien</strong>, no el grip máximo — en iniciación eso pesa más.',
    regular: 'Juegas cada semana: este es el <strong>punto dulce de calidad-precio</strong> para ese volumen.',
    competitivo: 'Compites en serio, y ahí el <strong>agarre extra sí se nota</strong> en las atajadas y en la confianza.',
  }[a.nivel];
  if (nivelTxt) r.push(nivelTxt);

  if (g.corte && a.corte && g.corte === a.corte) {
    r.push(`Trae el <strong>${CUT_LABEL[g.corte]}</strong> que pediste.`);
  } else if (g.corte && a.corte && (CUT_NEAR[a.corte] || []).includes(g.corte)) {
    r.push(`Pediste otra sensación de ajuste, pero de lo que tenemos en tu rango el <strong>${CUT_LABEL[g.corte]}</strong> es lo más cercano.`);
  } else if (g.corte) {
    r.push(`Trae <strong>${CUT_LABEL[g.corte]}</strong>.`);
  }

  if (a.superficie === 'sintetico') {
    r.push('Ojo con el sintético: es <strong>mucho más abrasivo</strong> que el pasto natural. Sea cual sea el guante, ahí dura menos — no es defecto, es la cancha.');
  } else if (a.superficie === 'dura') {
    r.push('En cancha dura conviene palma robusta y guante con más armado, por comodidad y vida útil.');
  } else if (a.superficie === 'natural') {
    r.push('En pasto natural los látex suaves rinden su mejor agarre, así que ahí le vas a sacar todo.');
  }

  // Nada de clima aquí: el quiz no pregunta por lluvia, así que afirmar "es bueno en
  // mojado" no respondería a nada que el portero nos haya dicho. El dato existe (g.wet)
  // y se usa como desempate, pero no se presume como razón. Si algún día se agrega la
  // pregunta de clima, este es el lugar donde entra.

  r.push('Y algo que casi nadie te dice: <strong>pagar más compra mejor agarre, no más meses de vida</strong>. La duración la pone la superficie donde juegas y el cuidado que le des.');

  return r;
}

function sizeFor(cm) {
  for (const row of SIZES) if (cm >= row.min && cm <= row.max) return row.size;
  return null;
}

/* ── Estado + UI ────────────────────────────────────────────────────────────── */

const answers = { para: null, nivel: null, frecuencia: null, superficie: null, presupuesto: null, corte: null };
let step = 0;
let current = null;
let hasRevealed = false; // la cascada del resultado corre solo en el primer revelado

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

const steps = $$('[data-gi-step]');
const TOTAL = steps.length;

function track(name, params) {
  if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
}

function showStep(i) {
  const next = Math.max(0, Math.min(i, TOTAL - 1));
  // Dirección del motion: avanzar desliza desde la derecha, regresar desde la izquierda.
  $('[data-gi-steps]').dataset.giDir = next < step ? 'back' : 'fwd';
  step = next;
  steps.forEach((el, idx) => (el.hidden = idx !== step));
  $('[data-gi-progress]').hidden = false;
  $('[data-gi-nav]').hidden = step === 0;
  $('[data-gi-step-num]').textContent = step + 1;
  $('[data-gi-step-total]').textContent = TOTAL;
  $('[data-gi-progress-fill]').style.width = ((step) / TOTAL) * 100 + '%';
  $('[data-gi-result]').hidden = true;
  $('[data-gi-steps]').hidden = false;
  const q = steps[step].querySelector('.gi-step__q');
  if (q) q.setAttribute('tabindex', '-1'), q.focus({ preventScroll: true });
}

function card(g, opts) {
  const o = opts || {};
  const img = g.img ? `<img src="${g.img}" alt="${g.t}" loading="lazy" width="180" height="180">` : '';
  return `
    <a class="gi-prod" href="/products/${g.h}" data-gi-prod="${g.h}">
      <div class="gi-prod__media">${img}</div>
      <div class="gi-prod__body">
        <p class="gi-prod__brand">${g.v}</p>
        <h4 class="gi-prod__name">${g.t}</h4>
        <p class="gi-prod__meta">
          ${g.corte ? `<span class="gi-tag">${CUT_LABEL[g.corte]}</span>` : ''}
          ${o.badge ? `<span class="gi-tag gi-tag--soft">${o.badge}</span>` : ''}
        </p>
        <p class="gi-prod__price">${money(g.p)}</p>
        <span class="gi-prod__cta">Ver guante <span class="uk-arrow" aria-hidden="true"></span></span>
      </div>
    </a>`;
}

function waLink() {
  const num = ($('[data-gi-wa]') && $('[data-gi-wa]').dataset.num) || '524773904522';
  const partes = [];
  if (answers.para) partes.push(answers.para === 'nino' ? 'para un niño' : 'para mí');
  if (answers.nivel) partes.push({ empezando: 'voy empezando', regular: 'juego cada semana', competitivo: 'compito en serio' }[answers.nivel]);
  if (answers.frecuencia) partes.push({ baja: 'juego fines de semana', media: '2-3 veces por semana', alta: 'casi diario' }[answers.frecuencia]);
  if (answers.superficie) partes.push('en ' + { natural: 'pasto natural', sintetico: 'sintético', dura: 'cancha dura', todas: 'de todo' }[answers.superficie]);
  const nombre = current && current.primary ? current.primary.t : '';
  const msg =
    'Hola, usé el asesor Tu Guante Ideal en su página.\n\n' +
    'Mi perfil: ' + partes.join(', ') + '.\n' +
    (nombre ? 'Me recomendó: ' + nombre + '.\n\n' : '\n') +
    'Tengo una duda:';
  return 'https://wa.me/' + num + '?text=' + encodeURIComponent(msg);
}

/* ── Frescura ───────────────────────────────────────────────────────────────
 * El snapshot (guante-ideal-data.js) envejece entre regeneraciones. Al pintar
 * corregimos precio y existencia contra /products/{handle}.js — la misma fuente
 * viva que la ficha del producto — y el snapshot queda de fallback sin red.
 * Un agotado se saca de G: si era el titular se recalcula la recomendación. */
const FRESH = new Map();
function freshData(h) {
  if (!FRESH.has(h)) {
    FRESH.set(h, fetch('/products/' + h + '.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => (p ? { p: Math.round(p.price / 100), ok: !!p.available } : null))
      .catch(() => null));
  }
  return FRESH.get(h);
}

function freshen() {
  $$('[data-gi-prod]').forEach((el) => {
    freshData(el.dataset.giProd).then((f) => {
      if (!f) return;
      const g = G.find((x) => x.h === el.dataset.giProd);
      if (g) g.p = f.p;
      const priceEl = el.querySelector('.gi-prod__price');
      if (priceEl) priceEl.textContent = money(f.p);
      if (!f.ok) {
        const i = G.findIndex((x) => x.h === el.dataset.giProd);
        if (i >= 0) G.splice(i, 1);
        const second = el.closest('[data-gi-second]');
        if (el.closest('[data-gi-primary]')) render();
        else if (second) second.hidden = true;
        else el.style.display = 'none';
      }
    });
  });
}

function render() {
  const res = recommend(answers);
  current = res;

  $('[data-gi-steps]').hidden = true;
  $('[data-gi-nav]').hidden = true;
  $('[data-gi-progress-fill]').style.width = '100%';
  const resEl = $('[data-gi-result]');
  resEl.classList.toggle('is-reveal', !hasRevealed);
  hasRevealed = true;
  resEl.hidden = false;

  if (!res) {
    $('[data-gi-primary]').innerHTML =
      `<div class="gi-empty">
         <p>Con ese perfil exacto no tenemos nada en existencia ahorita — y preferimos decírtelo
         a recomendarte algo que no te queda.</p>
         <a class="gi-btn gi-btn--primary" href="${waLink()}" target="_blank" rel="noopener">Escríbenos y te conseguimos</a>
       </div>`;
    $('[data-gi-why]').innerHTML = '';
    $('[data-gi-second]').hidden = true;
    $('[data-gi-alts]').hidden = true;
    track('guante_ideal_sin_resultado', answers);
    return;
  }

  $('[data-gi-primary]').innerHTML = card(res.primary);
  $('[data-gi-why]').innerHTML =
    '<ul class="gi-why__list">' + buildWhy(answers, res.primary).map((x) => `<li>${x}</li>`).join('') + '</ul>';

  const sec = $('[data-gi-second]');
  if (res.second) {
    sec.hidden = false;
    $('[data-gi-second-title]').textContent = res.second.titulo;
    $('[data-gi-second-body]').textContent = res.second.body;
    $('[data-gi-second-card]').innerHTML = card(res.second.g, { badge: 'para entrenar' });
  } else {
    sec.hidden = true;
  }

  if (res.alts.length) {
    $('[data-gi-alts]').hidden = false;
    $('[data-gi-alts-grid]').innerHTML = res.alts.map((g) => card(g)).join('');
  } else {
    $('[data-gi-alts]').hidden = true;
  }

  const wa = $('[data-gi-wa]');
  if (wa) wa.href = waLink();

  renderSize();

  track('guante_ideal_resultado', {
    ...answers,
    recomendado: res.primary.h,
    marca: res.primary.v,
    precio: res.primary.p,
    segundo_par: res.second ? res.second.g.h : '',
  });

  freshen();

  $('[data-gi-result]').scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* Talla + ajuste por corte. El KB es explícito: "el negativo queda más ceñido y el
 * plano/roll más holgados — con negativo muchos porteros piden media talla más". */
function renderSize() {
  const out = $('[data-gi-size]');
  const raw = $('[data-gi-cm]').value;
  if (!raw) { out.hidden = true; return; }

  const cm = Math.round(parseFloat(String(raw).replace(',', '.')) * 10) / 10;
  if (isNaN(cm) || cm <= 0) {
    out.hidden = false;
    out.className = 'gi-size-out gi-size-out--warn';
    out.innerHTML = 'Ingresa una medida válida en centímetros.';
    return;
  }

  let size = sizeFor(cm);
  let pre = '';
  if (cm < SIZES[0].min) { size = SIZES[0].size; pre = 'Tu medida está por debajo de la tabla, así que te va la más chica: '; }
  else if (cm > SIZES[SIZES.length - 1].max) { size = SIZES[SIZES.length - 1].size; pre = 'Tu medida se sale por arriba de la tabla, así que te va la más grande: '; }

  const corte = current && current.primary ? current.primary.corte : null;
  let nota = '';
  if (corte === 'negativo') {
    nota = 'Ojo: el guante que te recomendamos es de <strong>corte negativo</strong>, que queda pegado a la mano. Muchos porteros piden <strong>media talla más</strong> en negativo. Si tienes mano ancha, considéralo.';
  } else if (corte === 'roll' || corte === 'hibrido') {
    nota = 'El <strong>' + CUT_LABEL[corte] + '</strong> deja más aire que un negativo, así que tu talla de tabla debería quedarte bien.';
  }
  if (answers.para === 'nino') {
    nota += (nota ? ' ' : '') + 'Y como está creciendo: deja <strong>máximo 1 cm</strong> libre en las puntas. Más grande que eso se le mueve dentro de la mano y pierde control. Re-mide cada temporada.';
  }

  out.hidden = false;
  out.className = 'gi-size-out gi-size-out--ok';
  out.innerHTML =
    `<p class="gi-size-out__main">${pre}tu talla es la <span class="gi-size-badge">${size}</span></p>` +
    (nota ? `<p class="gi-size-out__note">${nota}</p>` : '');

  track('guante_ideal_talla', { cm, talla: size, corte: corte || '' });
}

/* ── Eventos ────────────────────────────────────────────────────────────────── */

$$('[data-gi-step] .gi-opt').forEach((btn) => {
  btn.addEventListener('click', () => {
    const fs = btn.closest('[data-gi-step]');
    const key = fs.dataset.giQ;
    answers[key] = btn.dataset.giA;

    fs.querySelectorAll('.gi-opt').forEach((b) => b.classList.remove('is-on'));
    btn.classList.add('is-on');

    track('guante_ideal_paso', { paso: key, valor: btn.dataset.giA });

    // Respiro corto para que se vea la selección antes de avanzar.
    setTimeout(() => { (step >= TOTAL - 1) ? render() : showStep(step + 1); }, 180);
  });
});

$$('.gi-chip').forEach((chip) => {
  chip.addEventListener('click', () => {
    answers.corte = chip.dataset.giA || null;
    $$('.gi-chip').forEach((c) => c.classList.remove('is-on'));
    chip.classList.add('is-on');
    track('guante_ideal_afinar_corte', { corte: answers.corte || 'no-se' });
    render();
  });
});

const cmGo = $('[data-gi-cm-go]');
if (cmGo) cmGo.addEventListener('click', renderSize);
const cmIn = $('[data-gi-cm]');
if (cmIn) cmIn.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); renderSize(); } });

const back = $('[data-gi-back]');
if (back) back.addEventListener('click', () => showStep(step - 1));

const start = $('[data-gi-start]');
if (start) start.addEventListener('click', (e) => { e.preventDefault(); showStep(0); $('#asesor').scrollIntoView({ behavior: 'smooth' }); track('guante_ideal_inicio'); });

const restart = $('[data-gi-restart]');
if (restart) {
  restart.addEventListener('click', () => {
    Object.keys(answers).forEach((k) => (answers[k] = null));
    current = null;
    hasRevealed = false; // corrida nueva = revelado nuevo, con su cascada
    $$('.gi-opt, .gi-chip').forEach((b) => b.classList.remove('is-on'));
    if (cmIn) cmIn.value = '';
    $('[data-gi-size]').hidden = true;
    showStep(0);
    $('#asesor').scrollIntoView({ behavior: 'smooth' });
    track('guante_ideal_reinicio');
  });
}

/* Honestidad en el hero: el conteo sale del catálogo cargado, no es un número inventado. */
const count = $('[data-gi-count]');
if (count && G.length) count.textContent = `${G.length} guantes en existencia`;

const waBtn = $('[data-gi-wa]');
if (waBtn) waBtn.href = waLink();
